/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { Layers, Check, X, RefreshCw } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { useToast } from '../../hooks/use-toast';
import { SkillShopService } from '../../services/skillShopService';
import { JiraConfig } from '../../types';

interface ConnectionStatus {
  tested: boolean;
  success: boolean;
  error?: string;
}

export function JiraSettings() {
  const { 
    jiraConfig, 
    updateJiraConfig, 
    clearJiraConfig 
  } = useApp();
  const { toast } = useToast();
  const [skillShopService] = useState(() => new SkillShopService());

  const [formData, setFormData] = useState<JiraConfig>({
    url: '',
    username: '',
    apiToken: '',
    projectKey: '',
    issueType: 'Story',
    epicIssueType: 'Epic'
  });

  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({ 
    tested: false, 
    success: false 
  });

  const [projects, setProjects] = useState<Array<{ key: string; name: string }>>([]);
  const [issueTypes, setIssueTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // Load existing config
  useEffect(() => {
    if (jiraConfig) {
      setFormData(jiraConfig);
    }
  }, [jiraConfig]);

  const handleInputChange = (field: keyof JiraConfig, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setConnectionStatus({ tested: false, success: false });
  };

  const testConnection = async () => {
    if (!formData.url || !formData.username || !formData.apiToken) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please fill in URL, username, and API token'
      });
      return;
    }

    setIsTestingConnection(true);
    
    try {
      const result = await skillShopService.testJiraUserStoryConnection({
        url: formData.url,
        username: formData.username,
        apiToken: formData.apiToken,
        projectKey: formData.projectKey
      });
      
      if (result.success) {
        setConnectionStatus({ tested: true, success: true });
        loadProjects();
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          error: result.error
        });
      }
    } catch (error: any) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const loadProjects = async () => {
    setIsLoadingProjects(true);
    try {
      const result = await skillShopService.getJiraProjects({
        url: formData.url,
        username: formData.username,
        apiToken: formData.apiToken,
        projectKey: formData.projectKey
      });
      if (result.success && result.data) {
        setProjects(result.data);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadIssueTypes = async (projectKey: string) => {
    if (!formData.url || !formData.username || !formData.apiToken) return;

    try {
      const result = await skillShopService.getJiraIssueTypes({
        url: formData.url,
        username: formData.username,
        apiToken: formData.apiToken,
        projectKey
      });
      if (result.success && result.data) {
        setIssueTypes(result.data);
      }
    } catch (error) {
      console.error('Failed to load issue types:', error);
    }
  };

  const handleProjectChange = (projectKey: string) => {
    handleInputChange('projectKey', projectKey);
    if (projectKey) {
      loadIssueTypes(projectKey);
    } else {
      setIssueTypes([]);
    }
  };

  const saveConfiguration = () => {
    if (connectionStatus.tested && !connectionStatus.success) {
      const confirmed = confirm(
        'The connection test failed, but you can still save this configuration. ' +
        'You may need to verify the settings later. Do you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    updateJiraConfig(formData);
    toast({
      title: 'Configuration saved',
      description: 'Your JIRA settings have been updated.',
    });
  };

  const clearConfiguration = () => {
    if (confirm('Are you sure you want to clear the JIRA configuration?')) {
      clearJiraConfig();
      setFormData({
        url: '',
        username: '',
        apiToken: '',
        projectKey: '',
        issueType: 'Story',
        epicIssueType: 'Epic'
      });
      setConnectionStatus({ tested: false, success: false });
      setProjects([]);
      setIssueTypes([]);
    }
  };

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Layers size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>JIRA Integration</h3>
      </div>
      
      <div className="form-group">
        <label htmlFor="jira-url">JIRA URL</label>
        <input
          id="jira-url"
          type="url"
          placeholder="https://your-domain.atlassian.net"
          value={formData.url}
          onChange={(e) => handleInputChange('url', e.target.value)}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Your JIRA instance URL (e.g., https://company.atlassian.net)
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="jira-username">Username/Email</label>
        <input
          id="jira-username"
          type="email"
          placeholder="your-email@company.com"
          value={formData.username}
          onChange={(e) => handleInputChange('username', e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="jira-token">API Token</label>
        <input
          id="jira-token"
          type="password"
          placeholder="Your JIRA API token"
          value={formData.apiToken}
          onChange={(e) => handleInputChange('apiToken', e.target.value)}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Create an API token at{' '}
          <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer">
            https://id.atlassian.com/manage-profile/security/api-tokens
          </a>
        </small>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <button
          className="btn"
          onClick={testConnection}
          disabled={isTestingConnection || !formData.url || !formData.username || !formData.apiToken}
        >
          {isTestingConnection ? (
            <>
              <RefreshCw size={16} style={{ marginRight: '8px' }} className="spin" />
              Testing Connection...
            </>
          ) : (
            'Test Connection'
          )}
        </button>

        {connectionStatus.tested && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center' }}>
            {connectionStatus.success ? (
              <>
                <Check size={16} style={{ color: '#28a745', marginRight: '8px' }} />
                <span className="status-badge success">Connection successful</span>
              </>
            ) : (
              <>
                <X size={16} style={{ color: '#dc3545', marginRight: '8px' }} />
                <span className="status-badge error">
                  {connectionStatus.error || 'Connection failed'}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="project-key">Project Key</label>
        {connectionStatus.success && projects.length > 0 ? (
          <select
            id="project-key"
            value={formData.projectKey}
            onChange={(e) => handleProjectChange(e.target.value)}
            disabled={isLoadingProjects}
          >
            <option value="">Select a project...</option>
            {projects.map(project => (
              <option key={project.key} value={project.key}>
                {project.key} - {project.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            id="project-key"
            type="text"
            placeholder="Enter project key (e.g., SKBR)"
            value={formData.projectKey}
            onChange={(e) => handleInputChange('projectKey', e.target.value)}
          />
        )}
        {isLoadingProjects && (
          <small style={{ color: '#666' }}>Loading projects...</small>
        )}
        {!connectionStatus.success && (
          <small style={{ color: '#666', fontSize: '12px' }}>
            Test connection successfully to see available projects, or enter project key manually
          </small>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="issue-type">User Story Issue Type</label>
        {connectionStatus.success && formData.projectKey && issueTypes.length > 0 ? (
          <select
            id="issue-type"
            value={formData.issueType}
            onChange={(e) => handleInputChange('issueType', e.target.value)}
          >
            {issueTypes.map(type => (
              <option key={type.id} value={type.name}>
                {type.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            id="issue-type"
            type="text"
            placeholder="Enter issue type (e.g., Story, Task, Bug)"
            value={formData.issueType}
            onChange={(e) => handleInputChange('issueType', e.target.value)}
          />
        )}
        {!connectionStatus.success && (
          <small style={{ color: '#666', fontSize: '12px' }}>
            Test connection successfully to see available issue types, or enter manually
          </small>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="epic-issue-type">Epic Issue Type</label>
        {connectionStatus.success && formData.projectKey && issueTypes.length > 0 ? (
          <select
            id="epic-issue-type"
            value={formData.epicIssueType || 'Epic'}
            onChange={(e) => handleInputChange('epicIssueType', e.target.value)}
          >
            {issueTypes.map(type => (
              <option key={type.id} value={type.name}>
                {type.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            id="epic-issue-type"
            type="text"
            placeholder="Enter epic issue type (e.g., Epic)"
            value={formData.epicIssueType || 'Epic'}
            onChange={(e) => handleInputChange('epicIssueType', e.target.value)}
          />
        )}
        {!connectionStatus.success && (
          <small style={{ color: '#666', fontSize: '12px' }}>
            Issue type used for creating epics (usually "Epic")
          </small>
        )}
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          className="btn"
          onClick={saveConfiguration}
          disabled={!formData.url || !formData.username || !formData.apiToken}
        >
          Save JIRA Configuration
        </button>
        
        {jiraConfig && (
          <button
            className="btn btn-secondary"
            onClick={clearConfiguration}
          >
            Clear JIRA Configuration
          </button>
        )}
      </div>
    </div>
  );
}
